import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { HomeTail } from '../api';
import { chatRowTime } from '../chats';
import { ChatPicture, chatHref } from '../pages/Chats';
import { navigate } from '../router';

/** The change itself: 0.2 s out, then 0.3 s in (TreeHome.css); after it the one that left goes back below, unseen. */
const CHANGE_MS = 500;
/**
 * How long each conversation stays before the next (2026-10-20 founder, second word, mock ticker-motion B): 3.5 s
 * still and 0.5 s to change — the whole row sliding up every 3 s made the eye follow it and the second line ran out.
 */
const HOLD_MS = 4000;

/**
 * The bar over the composer (2026-10-20 founder, mock home-ticker option A; spec §0.8 A): the conversations with
 * something unread, one at a time — the conversation's picture, its name and when, how many are new, then who said
 * what, the same words the Chats list and the banner use. Each stays 3.5 seconds; then it fades out lifting 6px and
 * the next fades in from 6px below (TreeHome.css); a finger (or a mouse) on the bar holds it. A tap opens the one showing: Ember's chat the way the arrow does (`onEmber`, the
 * field's sentence going along), a room in the chat (the field stays as it is). With nothing unread it is Ember's last
 * line, still (edge chats.homeTail).
 */
export function HeroTail({ tail, onEmber, log }: { tail: HomeTail; onEmber: () => void; log: Record<string, string> }): ReactElement | null {
  const { rows } = tail;
  // `gone`: the one that just left, lifting out; once out it waits below with the rest, so the next one in always
  // rises from below (with two rows, the one leaving is the next to come back).
  const [{ at, gone }, setTurn] = useState({ at: 0, gone: -1 });
  const held = useRef(false);
  useEffect(() => {
    setTurn({ at: 0, gone: -1 });
    if (rows.length < 2) return undefined;
    const id = window.setInterval(() => { if (!held.current) setTurn((t) => ({ at: (t.at + 1) % rows.length, gone: t.at })); }, HOLD_MS);
    return () => window.clearInterval(id);
  }, [rows]);
  useEffect(() => {
    if (gone < 0) return undefined;
    const id = window.setTimeout(() => setTurn((t) => ({ ...t, gone: -1 })), CHANGE_MS);
    return () => window.clearTimeout(id);
  }, [gone]);
  const row = rows[at] ?? rows[0];
  if (!row) return null;
  const zone = tail.clock?.zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hold = (on: boolean) => () => { held.current = on; };
  const open = (): void => {
    if (row.kind === 'ember') { onEmber(); return; }
    const to = chatHref(row);
    if (to) navigate(to);
  };
  return (
    <button
      type="button"
      className="hero-tail"
      {...log}
      data-log="hero_thread_tail"
      data-log-seen="hero_thread_tail"
      data-log-state={row.kind}
      {...(row.kind === 'ember' ? {} : { 'data-log-match-id': row.room_id ?? '' })}
      onPointerEnter={hold(true)}
      onPointerDown={hold(true)}
      onPointerLeave={hold(false)}
      onPointerCancel={hold(false)}
      onClick={open}
    >
      <span className="hero-tail-track">
        {rows.map((r, i) => (
          <span key={r.key} className={`hero-tail-item${i === at ? ' is-on' : i === gone ? ' is-gone' : ''}`} aria-hidden={i !== at}>
            <ChatPicture kind={r.kind} people={r.kind === 'ember' ? [] : r.people} roomId={r.kind === 'ember' ? null : r.room_id} roomKind={r.kind === 'ember' ? null : r.room_kind} />
            <span className="hero-tail-text">
              <span className="hero-tail-head">
                <b>{r.name}</b>
                <small>{chatRowTime(r.last?.at ?? r.at, zone)}</small>
                {r.unread > 0 ? <em>{r.unread} new</em> : null}
              </span>
              <span className="hero-tail-line">
                {r.kind === 'ember' ? null : <b>{r.last?.who}: </b>}
                {r.last?.text}
              </span>
            </span>
          </span>
        ))}
      </span>
      <span className="hero-tail-chev" aria-hidden>›</span>
      {rows.length > 1 ? <span className="hero-tail-dots" aria-hidden>{rows.map((r, i) => <i key={r.key} className={i === at ? 'is-on' : ''} />)}</span> : null}
    </button>
  );
}
